import { api } from './client';

// ============================================================================
//  Simulated A-share trading client (mounted under /api/sim).
// ============================================================================

export interface SimPosition {
  id: number;
  ticker: string;
  name: string;
  qty: number;          // shares (integer multiple of 100)
  avgCost: number;
  currentPrice: number; // 0 if no quote snapshot
  marketValue: number;
  pnl: number;
  pnlPct: number;
  openedAt: number;     // unix seconds
}

export interface SimAccount {
  id: number;
  userId: number;
  initialCash: number;
  cashBalance: number;
  totalMarketValue: number;
  totalValue: number;     // cashBalance + totalMarketValue
  totalCost: number;
  totalPnL: number;
  totalReturn: number;    // %
  resetCount: number;
  positions: SimPosition[];
  createdAt: number;
  updatedAt: number;
}

export interface SimOrder {
  id: number;
  accountId: number;
  ticker: string;
  name: string;
  side: 'buy' | 'sell';
  qty: number;
  fillPrice: number;
  status: 'filled' | 'rejected';
  reason: string;
  placedAt: number;
  filledAt: number;
  // Backend enrichment for filled SELL rows only — replay of the
  // running-VWAP cost basis at the moment of the sell, plus the
  // resulting realized PnL (qty × (fillPrice − costBasis)).
  // Buys / rejections leave both fields undefined.
  costBasis?: number;
  realizedPnL?: number;
}

export const getSimAccount = () =>
  api.get<SimAccount>('/sim/account');

export const transferSimAccount = (delta: number) =>
  api.patch<SimAccount>('/sim/account/transfer', { delta });

export const resetSimAccount = () =>
  api.post<SimAccount>('/sim/account/reset', {});

export const listSimPositions = () =>
  api.get<{ positions: SimPosition[] }>('/sim/positions');

export const listSimOrders = (limit = 100) =>
  api.get<{ orders: SimOrder[] }>(`/sim/orders?limit=${limit}`);

// placeSimOrder issues a market buy/sell. Returns either the filled order or
// a rejection (the backend always persists the SimOrder row, even on errors,
// and surfaces the reason in the message field).
export const placeSimOrder = (body: { ticker: string; side: 'buy' | 'sell'; qty: number; price?: number }) =>
  api.post<{ order: SimOrder; fillPrice: number }>('/sim/orders', body);

// AI 复核 — pre-flight check before the user commits. 30-min server cache
// per (user, ticker, side, qty bucket); trace persists with stage
// "user_order_review" and is reachable via getLLMTrace(traceId).
export interface OrderReviewResult {
  verdict: '建议执行' | '谨慎执行' | '建议放弃';
  confidence: number;     // 1-5
  keyRisk: string;        // 60-120 字
  suggestion: string;     // optional adjustment, e.g. "改成 50 股"
  traceId: number;        // 0 when no session attribution; UI then hides 思考链 link
  stage: string;
  cached: boolean;
  issuedAt: number;
}

export const previewSimOrder = (body: {
  ticker: string;
  side: 'buy' | 'sell';
  qty: number;
  sessionId?: number;
}) => api.post<OrderReviewResult>('/sim/orders/preview', body);
